import React from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const PAISLEY = [55.8456, -4.4239];

const COLOURS = {
  high: '#dc2626',
  medium: '#d97706',
  low: '#64748b',
};

// CARTO's cleaner basemap when a key is configured, otherwise plain OpenStreetMap.
// Note: this must be a CARTO *basemap* key from carto.com/basemaps/apikey/ —
// a CARTO platform API token is a different credential and is ignored here.
const CARTO_KEY = process.env.REACT_APP_CARTO_API_KEY;

const TILES = CARTO_KEY
  ? {
      url: `https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 20,
    }
  : {
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    };

/**
 * Planned closures plotted from the Scottish Road Works Register. Street works
 * are already public on the national register, so these pins are free-tier.
 */
const RoadworksMap = ({ points = [] }) => {
  const plotted = points.filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number');
  if (!plotted.length) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white"
      data-testid="iw-roadworks-map"
    >
      <MapContainer
        center={PAISLEY}
        zoom={11}
        scrollWheelZoom={false}
        style={{ height: '420px', width: '100%' }}
        attributionControl
      >
        <TileLayer
          key={TILES.url}
          url={TILES.url}
          attribution={TILES.attribution}
          maxZoom={TILES.maxZoom}
        />
        {plotted.map((p) => (
          <CircleMarker
            key={p.id}
            center={[p.lat, p.lng]}
            radius={p.trunk_road ? 9 : 6}
            pathOptions={{
              color: COLOURS[p.severity] || COLOURS.low,
              fillColor: COLOURS[p.severity] || COLOURS.low,
              fillOpacity: 0.55,
              weight: p.trunk_road ? 3 : 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <span className="text-xs font-semibold">{p.label}</span>
              {p.trunk_road ? <span className="block text-[10px] text-sky-700">Trunk road</span> : null}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
        {[['high', 'Major closure'], ['medium', 'Closure or controls'], ['low', 'Minor']].map(([key, label]) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOURS[key] }} />
            {label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full ring-2 ring-slate-400" />
          Larger ring = trunk road
        </span>
        <span className="ml-auto">{plotted.length} works plotted</span>
      </div>
    </div>
  );
};

export default RoadworksMap;
